import type { ReactNode, MouseEvent } from 'react';
import { Checkbox, useSlotRecipe, type CheckboxRootProps } from '@chakra-ui/react';
import { checkboxSlotRecipe } from '../../theme/recipes/checkbox';

const FriendlyCheck = (
  <svg viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M3.4 8.3 6.5 11.3 12.7 4.6"
      stroke="currentColor"
      strokeWidth="2.1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type RiftboundCheckboxProps = Omit<CheckboxRootProps, 'colorPalette' | 'variant'> & {
  /** Visible label. Prefer `aria-label` when the control stands alone. */
  children?: ReactNode;
  /**
   * `overlay` sits on card art: opaque fill and a dark halo so gold fill
   * and the dark check stay readable on both light and dark illustrations.
   */
  tone?: 'default' | 'overlay';
};

/**
 * Shared checkbox. Checked fill and the overlay halo live in the checkbox slot recipe.
 */
export function RiftboundCheckbox({
  children,
  size = 'md',
  tone = 'default',
  'aria-label': ariaLabel,
  onClick,
  ...rest
}: RiftboundCheckboxProps) {
  const recipe = useSlotRecipe({ recipe: checkboxSlotRecipe });
  const styles = recipe({ variant: tone });

  const handleClick = (event: MouseEvent<HTMLLabelElement>) => {
    event.stopPropagation();
    onClick?.(event);
  };

  return (
    <Checkbox.Root
      size={size}
      variant="solid"
      colorPalette="brandGold"
      cursor="pointer"
      flexShrink={0}
      onClick={handleClick}
      css={styles.root}
      {...rest}
    >
      <Checkbox.HiddenInput aria-label={ariaLabel} />
      <Checkbox.Control css={styles.control}>
        <Checkbox.Indicator checked={FriendlyCheck} />
      </Checkbox.Control>
      {children ? <Checkbox.Label css={styles.label}>{children}</Checkbox.Label> : null}
    </Checkbox.Root>
  );
}
